import React from "react";
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from "react-vertical-timeline-component";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import { buildTimelineTree } from "../utils/timelineTree";
import "react-vertical-timeline-component/style.min.css";

// Default card rendered inside each timeline element.
// Replace this by passing a renderCard prop: (event) => <YourCard event={event} />
const DefaultTimelineCard = ({ event }) => {
  const {
    title,
    company,
    mainTech = [],
    technologies = [],
    description,
    nestedEvents = [],
  } = event;
  return (
    <div className="timeline-record">
      <div className="mb-5 flex items-center justify-between gap-4 border-b border-line pb-3">
        <span className="eyebrow">{event.type || "record"}</span>
        <span className="font-mono text-[9px] uppercase tracking-[.1em] text-muted">
          Entry / {String(event.sortKey ?? "--").padStart(2, "0")}
        </span>
      </div>
      {mainTech.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {mainTech.map((tech, i) => (
            <span className="timeline-badge timeline-badge-main" key={i}>{tech}</span>
          ))}
        </div>
      )}
      <h3 className="vertical-timeline-element-title text-left font-display text-xl font-semibold tracking-[-0.025em] text-textmain md:text-2xl">{title}</h3>
      <h4 className="vertical-timeline-element-subtitle mt-2 text-left font-mono text-[10px] uppercase tracking-[.09em] text-accent">{company}</h4>
      {description && <p className="mt-4 text-sm leading-7 text-muted">{description}</p>}
      {technologies.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-1.5 border-t border-line pt-4">
          {technologies.map((tech, i) => (
            <span className="timeline-badge" key={i}>{tech}</span>
          ))}
        </div>
      )}
      {nestedEvents.length > 0 && (
        <div className="mt-6 border-t border-line pt-5">
          <p className="eyebrow mb-3">Within this record</p>
          <div className="space-y-3">
            {nestedEvents.map((nestedEvent) => (
              <NestedTimelineRecord event={nestedEvent} key={nestedEvent.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Generic vertical timeline.
 *
 * Props:
 *   events      {Array}    Required. Each item: { title, company, years, mainTech[], technologies[], description? }
 *   renderCard  {Function} Optional. (event) => ReactNode — provide a custom card per item.
 *   iconStyle   {Object}   Optional. Override the icon bubble style.
 *   icon        {ReactNode} Optional. Override the icon element.
 */
const getEventIcon = (event) => {
  if (event?.icon) {
    return event.icon;
  }

  if (event?.type === "education") {
    return <SchoolOutlinedIcon className="timeline-marker-icon" />;
  }

  return <WorkOutlineIcon className="timeline-marker-icon" />;
};

const NestedTimelineRecord = ({ event }) => {
  const {
    company,
    description,
    mainTech = [],
    nestedEvents = [],
    technologies = [],
    title,
    years,
  } = event;
  const badges = [...new Set([...mainTech, ...technologies])];

  return (
    <details className={`timeline-nested ${nestedEvents.length > 0 ? "" : "timeline-nested--leaf"}`} open>
      <summary className="timeline-nested-summary">
        <span className="timeline-nested-icon" aria-hidden="true">{getEventIcon(event)}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-textmain">{title}</span>
          <span className="mt-1 block font-mono text-[9px] uppercase leading-4 tracking-[.08em] text-muted">
            {company}{years ? ` · ${years}` : ""}
          </span>
        </span>
        <span className="timeline-nested-chevron" aria-hidden="true">▾</span>
      </summary>
      <div className="timeline-nested-body">
        {description && <p className="text-sm leading-6 text-muted">{description}</p>}
        {badges.length > 0 && (
          <div className={`${description ? "mt-3" : ""} flex flex-wrap gap-1.5`}>
            {badges.map((badge, index) => (
              <span className="timeline-badge" key={`${badge}-${index}`}>{badge}</span>
            ))}
          </div>
        )}
        {nestedEvents.length > 0 && (
          <div className="mt-5 border-t border-line pt-4">
            <p className="eyebrow mb-3">During this record</p>
            <div className="space-y-3">
              {nestedEvents.map((nestedEvent) => (
                <NestedTimelineRecord event={nestedEvent} key={nestedEvent.id} />
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
};

const Timeline = ({
  events = [],
  renderCard,
  iconStyle,
  endIcon,
}) => {
  const bubbleStyle = iconStyle || {
    background: "var(--accent)",
    color: "var(--marker-ink)",
    border: "1px solid var(--accent)",
    boxShadow: "0 0 0 6px var(--bg)",
  };

  const orderedEvents = buildTimelineTree(events);

  return (
    <div className="precision-timeline">
    <VerticalTimeline animate lineColor="var(--line)">
      
      {orderedEvents.map((event, i) => (
        <VerticalTimelineElement
          className={`vertical-timeline-element--${event.type || "record"}`}
          date={event.years}
          contentStyle={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--line)", boxShadow: "none", borderRadius: "var(--radius-lg)" }}
          contentArrowStyle={{ borderRight: "7px solid var(--line)" }}
          dateClassName="timeline-date"
          iconStyle={bubbleStyle}
          icon={getEventIcon(event)}
          key={event.id || i}
        >
          {renderCard ? renderCard(event) : <DefaultTimelineCard event={event} />}
        </VerticalTimelineElement>
      ))}
      <VerticalTimelineElement
        className="vertical-timeline-element--future"
        contentStyle={{ background: "var(--surface-2)", color: "var(--text)", border: "1px dashed var(--line)", boxShadow: "none", borderRadius: "var(--radius-lg)" }}
        contentArrowStyle={{ borderRight: "7px solid var(--line)" }}
        iconStyle={bubbleStyle}
        icon={endIcon || <HourglassBottomOutlinedIcon className="timeline-marker-icon" />}
      >
        <p className="eyebrow mb-2">Next record</p>
        <h4 className="vertical-timeline-element-subtitle text-left text-sm leading-6 text-muted">Waiting for future achievements and experiences.</h4>
      </VerticalTimelineElement>
    </VerticalTimeline>
    </div>
  );
};

export default Timeline;
